/**
 * V3 homepage section order — TREATX-V3-SECTION-ORDER-01
 * ======================================================
 * ONE canonical ordering authority: the `display_order` already carried by each
 * V3-scoped `section_copy` record. This module only supplies the DEFAULT value a
 * section has before the owner ever saves an order, and the rule for turning a
 * stored value into an effective one.
 *
 * Values are SPARSE (10, 20, 30, ...) so a section can be moved between two
 * others by typing a number — 30 -> 19 lands it before 20 — without renumbering
 * anything else.
 *
 * `hero` is anchored: it carries the search panel directly under the site header
 * and is rendered outside the ordered list, so it cannot be moved.
 */

export const V3_SECTION_DEFAULT_ORDER = {
  hero: 0,                 // anchored, never reordered
  how_it_works: 20,
  treatments: 30,
  providers: 40,
  showcase: 50,
  why: 60,
  certifications: 70,
  provider_cta: 80,        // the Founding programme is nested inside this one
  academy: 90,
  apps: 100,
  final_cta: 110,
};

export const V3_ORDERABLE_SECTIONS = Object.keys(V3_SECTION_DEFAULT_ORDER)
  .filter((k) => k !== 'hero');

/**
 * Effective order of a section.
 *
 * A stored value wins, EXCEPT the legacy 1..13 slot index that early adoption
 * wrote (it equals the section's position, not a sparse value): that is read as
 * "no explicit order yet" so existing records keep their current relative
 * order until the owner saves once. Nothing is rewritten silently.
 */
export const effectiveSectionOrder = (key, storedOrder) => {
  const base = V3_SECTION_DEFAULT_ORDER[key] ?? 999;
  const n = Number(storedOrder);
  if (!Number.isFinite(n) || n <= 0) return base;
  if (n < 14) return base;           // legacy slot index, not a sparse order
  return n;
};

/** Ascending order, ties broken by the built-in position — always deterministic. */
export const compareSections = (a, b) => (
  effectiveSectionOrder(a.key, a.storedOrder) - effectiveSectionOrder(b.key, b.storedOrder)
  || (V3_SECTION_DEFAULT_ORDER[a.key] ?? 999) - (V3_SECTION_DEFAULT_ORDER[b.key] ?? 999)
  || a.key.localeCompare(b.key)
);
